import React, { useState, useEffect, useRef } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { FiSearch, FiCheck } from 'react-icons/fi';

const Dropdown = ({
	label,
	options,
	value,
	onChange,
	onSearch,
	searchable,
	disabled = false,
	showAllOption = true,
	placeholder = 'Search...',
	minWidth = '200px',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [hoveredIndex, setHoveredIndex] = useState(-1);
	const dropdownRef = useRef(null);
	let searchTimeout;

	const handleSelect = (val) => {
		onChange(val);
		setIsOpen(false);
		setSearchTerm('');
		setHoveredIndex(-1);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchTerm('');
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		clearTimeout(searchTimeout);
		if (searchable) {
			searchTimeout = setTimeout(() => {
				onSearch(searchTerm);
			}, 500);
		}
	}, [searchTerm]);

	const selectedOption = options.find((opt) => opt.value == value);

	return (
		<div className='relative filter-dropdown w-full' ref={dropdownRef}>
			<div className='relative'>
				{/* Bouton principal avec animations */}
				<div
					className={`group mt-1 block w-full pl-4 pr-3 py-3 text-base border-2 
						dark:bg-secondary-dark-bg text-white rounded-xl shadow-md cursor-pointer 
						transition-all duration-300 ease-out
						${isOpen
							? 'border-indigo-500 shadow-lg shadow-indigo-500/20 scale-[1.02]'
							: 'border-gray-300 hover:border-indigo-400 hover:shadow-lg'
						}
						flex justify-between items-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
					onClick={() => { if (disabled) return; setIsOpen((prev) => !prev) }}
					style={{ minWidth }}
				>
					<span className='truncate font-medium flex items-center gap-2'>
						{selectedOption && (
							<span className='w-2 h-2 rounded-full bg-indigo-500 animate-pulse' />
						)}
						{value ? selectedOption?.label : label}
					</span>
					<IoIosArrowDown
						className={`text-white transition-all duration-300 text-lg
							${isOpen ? 'rotate-180 text-indigo-400' : 'group-hover:translate-y-0.5'}
						`}
					/>
				</div>

				{/* Menu déroulant avec animations */}
				{isOpen && (
					<div
						className='absolute z-10 w-full mt-2 bg-white dark:bg-secondary-dark-bg 
							border-2 border-indigo-500 rounded-xl shadow-2xl shadow-indigo-500/10
							max-h-80 overflow-hidden backdrop-blur-sm
							animate-slideDown origin-top'
						style={{ minWidth }}
					>
						{/* Barre de recherche stylisée */}
						{searchable && (
							<div className='relative border-b border-gray-700 bg-gradient-to-r from-indigo-500/5 to-purple-500/5'>
								<FiSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 text-lg' />
								<input
									type='text'
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder={placeholder}
									className='block w-full pl-10 pr-4 py-3 text-base bg-transparent text-white 
										placeholder-gray-400 focus:outline-none sm:text-sm rounded-t-xl
										transition-all duration-200'
									autoFocus
								/>
								{searchTerm && (
									<div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
										<div className='w-5 h-5 border-2 border-indigo-400 border-t-transparent 
											rounded-full animate-spin' />
									</div>
								)}
							</div>
						)}

						{/* Liste des options avec animations */}
						<ul className='py-2 overflow-auto max-h-60 scrollbar-thin scrollbar-thumb-indigo-500 
							scrollbar-track-gray-800'>
							{showAllOption && (
								<li
									className='group cursor-pointer px-4 py-2.5 text-sm text-white
										transition-all duration-200 flex items-center justify-between
										hover:bg-gradient-to-r hover:from-indigo-500/20 hover:to-purple-500/20
										hover:pl-6 relative overflow-hidden'
									onClick={() => handleSelect('')}
									onMouseEnter={() => setHoveredIndex(-1)}
								>
									<span className='flex items-center gap-3 font-medium'>
										<span className='w-2 h-2 rounded-full bg-gray-400 
											group-hover:bg-indigo-400 transition-colors' />
										Tous
									</span>
									{!value && <FiCheck className='text-indigo-400 animate-fadeIn' />}
									<div className='absolute left-0 top-0 h-full w-1 bg-indigo-500 
										transform scale-y-0 group-hover:scale-y-100 transition-transform 
										duration-300 origin-top' />
								</li>
							)}

							{options.length > 0 ? (
								options.map((option, index) => {
									const isSelected = value === option.value;
									const isHovered = hoveredIndex === index;

									return (
										<li
											key={index}
											className={`group cursor-pointer px-4 py-2.5 text-sm text-white
												transition-all duration-200 flex items-center justify-between
												hover:pl-6 relative overflow-hidden
												${isSelected
													? 'bg-gradient-to-r from-indigo-500/30 to-purple-500/30 font-semibold'
													: 'hover:bg-gradient-to-r hover:from-indigo-500/20 hover:to-purple-500/20'
												}`}
											onClick={() => handleSelect(option.value)}
											onMouseEnter={() => setHoveredIndex(index)}
											onMouseLeave={() => setHoveredIndex(-1)}
										>
											<span className='flex items-center gap-3 truncate'>
												<span className={`w-2 h-2 rounded-full transition-all duration-300
													${isSelected
														? 'bg-indigo-400 scale-125'
														: 'bg-gray-600 group-hover:bg-indigo-400'
													}`}
												/>
												{option.label}
											</span>
											{isSelected && (
												<FiCheck className='text-indigo-400 animate-checkmark' />
											)}
											{isHovered && !isSelected && (
												<div className='w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping' />
											)}
											<div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b 
												from-indigo-500 to-purple-500 transform transition-transform duration-300 origin-top
												${isSelected ? 'scale-y-100' : 'scale-y-0 group-hover:scale-y-100'}
											`} />
										</li>
									);
								})
							) : (
								<li className='px-4 py-8 text-gray-400 text-sm text-center'>
									<div className='flex flex-col items-center gap-2'>
										<div className='text-3xl opacity-50'>🔍</div>
										<span>No options found</span>
									</div>
								</li>
							)}
						</ul>
					</div>
				)}
			</div>

			{/* Styles CSS personnalisés */}
			<style jsx>{`
				@keyframes slideDown {
					from {
						opacity: 0;
						transform: translateY(-10px) scale(0.95);
					}
					to {
						opacity: 1;
						transform: translateY(0) scale(1);
					}
				}
				@keyframes fadeIn {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes checkmark {
					0% { transform: scale(0) rotate(-45deg); }
					50% { transform: scale(1.2) rotate(0deg); }
					100% { transform: scale(1) rotate(0deg); }
				}
				.animate-slideDown {
					animation: slideDown 0.2s ease-out forwards;
				}
				.animate-fadeIn {
					animation: fadeIn 0.3s ease-in;
				}
				.animate-checkmark {
					animation: checkmark 0.3s ease-out;
				}
				.scrollbar-thin::-webkit-scrollbar {
					width: 6px;
				}
				.scrollbar-thin::-webkit-scrollbar-track {
					background: #1f2937;
					border-radius: 10px;
				}
				.scrollbar-thin::-webkit-scrollbar-thumb {
					background: #6366f1;
					border-radius: 10px;
				}
				.scrollbar-thin::-webkit-scrollbar-thumb:hover {
					background: #818cf8;
				}
			`}</style>
		</div>
	);
};

export default Dropdown;